import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import LoadingSpinner from "@/components/loadingSpinner.component";
import { useAuth } from "@/lib/contexts/auth.context";
import { useLayout } from "@/lib/contexts/layout.context";
import { OrgaInvitationService } from "@/lib/invitation/invitation.service";
import type { OrganizationInvitationCheckResponseDTO } from "@/lib/invitation/invitation.types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEffect, useId, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

type InvitationState =
	| { status: "idle" | "loading" }
	| { status: "invalid" }
	| { status: "valid"; data: OrganizationInvitationCheckResponseDTO };

const InvitationPage = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { isAuthenticated, logout } = useAuth();
	const { setShowNavbar } = useLayout();
	const [state, setState] = useState<InvitationState>({ status: "idle" });
	const [isRedeeming, setIsRedeeming] = useState(false);
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [displayName, setDisplayName] = useState("");

	const displayNameId = useId();
	const passwordId = useId();
	const confirmPasswordId = useId();

	const invitationId = useMemo(
		() => searchParams.get("invitation") ?? "",
		[searchParams],
	);

	// Hide navbar while on this page
	useEffect(() => {
		setShowNavbar(false);
		return () => setShowNavbar(true);
	}, [setShowNavbar]);

	useEffect(() => {
		if (!invitationId) {
			setState({ status: "invalid" });
			return;
		}

		let isCancelled = false;
		const service = OrgaInvitationService.getInstance();
		setState({ status: "loading" });
		service
			.checkInvitationValidity(invitationId)
			.then((res) => {
				if (isCancelled) return;
				setState({ status: "valid", data: res });
			})
			.catch(() => {
				if (isCancelled) return;
				setState({ status: "invalid" });
			});

		return () => {
			isCancelled = true;
		};
	}, [invitationId]);

	const handleRedeem = async () => {
		if (state.status !== "valid") return;
		if (password.length < 8) {
			toast.error("Passwort muss mindestens 8 Zeichen lang sein");
			return;
		}
		if (password !== confirmPassword) {
			toast.error("Passwörter stimmen nicht überein");
			return;
		}
		setIsRedeeming(true);
		try {
			await OrgaInvitationService.getInstance().redeemInvitation(
				invitationId,
				password,
				displayName || undefined,
			);
			toast.success("Einladung eingelöst. Bitte melde dich jetzt an.");
			navigate("/auth/login");
		} catch (_) {
			toast.error("Einladung konnte nicht eingelöst werden");
		} finally {
			setIsRedeeming(false);
		}
	};

	// If a user is authenticated, show an error (invitation flow requires logged-out)
	if (isAuthenticated) {
		return (
			<div className="flex min-h-[60vh] items-center justify-center p-4">
				<Card className="max-w-xl w-full">
					<CardHeader>
						<CardTitle>Bereits angemeldet</CardTitle>
						<CardDescription>
							Diese Einladung kann nur genutzt werden, wenn Sie abgemeldet sind.
						</CardDescription>
					</CardHeader>
					<CardFooter className="gap-2">
						<Button variant="outline" onClick={() => navigate("/dashboard")}>
							Zum Dashboard
						</Button>
						<Button onClick={logout}>Abmelden</Button>
					</CardFooter>
				</Card>
			</div>
		);
	}

	// Render logic
	if (state.status === "idle" || state.status === "loading") {
		return <LoadingSpinner />;
	}

	if (state.status === "invalid") {
		return (
			<div className="flex min-h-[60vh] items-center justify-center p-4">
				<Card className="max-w-xl w-full">
					<CardHeader>
						<CardTitle>Einladung ungültig</CardTitle>
						<CardDescription>
							Diese Einladung ist ungültig oder abgelaufen.
						</CardDescription>
					</CardHeader>
					<CardFooter>
						<Button variant="outline" onClick={() => navigate("/")}>
							Zur Startseite
						</Button>
					</CardFooter>
				</Card>
			</div>
		);
	}

	if (state.status !== "valid") {
		return null;
	}

	const { data } = state;

	/**
	 * No matter what, the user needs to set a password.
	 * If the user is already registered, they need to set a password.
	 * If the user is not registered, they need to set a password.
	 */
	const isSubmitDisabled =
		isRedeeming || password.length < 8 || password !== confirmPassword;
	return (
		<div className="flex min-h-[60vh] items-center justify-center p-4">
			<Card className="max-w-xl w-full">
				<CardHeader>
					<CardTitle>Einladung</CardTitle>
					<CardDescription>
						Du wurdest zu „{data.invitation.organizationName}“ eingeladen. Bitte
						setze ein Passwort, um dein Konto zu erstellen.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor={displayNameId}>Anzeigename (optional)</Label>
						<Input
							id={displayNameId}
							type="text"
							value={displayName}
							onChange={(e) => setDisplayName(e.target.value)}
							placeholder="Max Mustermann"
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor={passwordId}>Passwort</Label>
						<Input
							id={passwordId}
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							placeholder="Mindestens 8 Zeichen"
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor={confirmPasswordId}>Passwort bestätigen</Label>
						<Input
							id={confirmPasswordId}
							type="password"
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
						/>
					</div>
				</CardContent>
				<CardFooter className="gap-2">
					<Button variant="outline" onClick={() => navigate("/")}>
						Abbrechen
					</Button>
					<Button onClick={handleRedeem} disabled={isSubmitDisabled}>
						{isRedeeming ? "Wird gespeichert..." : "Passwort setzen"}
					</Button>
				</CardFooter>
			</Card>
		</div>
	);
};

export default InvitationPage;
